export type RiskGapSource = 'zus-rules' | 'zus-ai' | 'payer-gap' | 'payer-claim' | 'user-update';
export type RiskGapType = 'recapture' | 'suspect' | 'net-new';
export type RiskGapSubstatus = {
  label: string;
  value: string;
};

export interface RiskGapICD10Code {
  code: string;
  display: string;
  lastEncounterDiagnosisRecordedDate?: string;
  sources: Array<string>;
}

// Single source of truth for all status options (user + system)
const RISK_GAP_STATUS_OPTIONS = [
  { label: 'Open', value: 'open-gap', kind: 'user' as const },
  { label: 'Pending', value: 'pending', kind: 'user' as const },
  { label: 'Accepted', value: 'accepted-for-review', kind: 'user' as const },
  { label: 'Dismissed', value: 'dismissed', kind: 'user' as const },
  {
    label: 'Expected to close',
    value: 'expected-to-close-1st-party',
    kind: 'system' as const,
  },
  {
    label: 'Expected to close',
    value: 'expected-to-close-3rd-party',
    kind: 'system' as const,
  },
  {
    label: 'Closed Gap',
    value: 'closed-gap',
    kind: 'system' as const,
  },
] as const;

type RiskGapStatusOption = (typeof RISK_GAP_STATUS_OPTIONS)[number];

export type RiskGapStatus = RiskGapStatusOption['value'];

// Substatus option shape for statuses that have substatuses
const substatusOption = (label: string, value: string): RiskGapSubstatus => ({ label, value });

// Exhaustive substatuses: one entry per status (empty array where none)
export const riskGapDefaultSubstatuses: Record<RiskGapStatus, ReadonlyArray<RiskGapSubstatus>> = {
  'open-gap': [],
  pending: [
    substatusOption('Sent to provider for review', 'sent-to-provider-for-review'),
    substatusOption('Condition being assessed', 'condition-being-assessed'),
    substatusOption('Other', 'other'),
  ],
  'accepted-for-review': [
    substatusOption('Sent to provider for review', 'sent-to-provider-for-review'),
    substatusOption('Accepted by provider', 'accepted-by-provider'),
    substatusOption('Condition being assessed', 'condition-being-assessed'),
    substatusOption('Other', 'other'),
  ],
  dismissed: [
    substatusOption('Acute in nature', 'acute-in-nature'),
    substatusOption('Inaccurate or insufficient evidence', 'inaccurate-or-insufficient-evidence'),
    substatusOption('Resolved', 'resolved'),
  ],
  'expected-to-close-1st-party': [],
  'expected-to-close-3rd-party': [],
  'closed-gap': [],
};

export interface RiskGapStatusUpdate {
  status: RiskGapStatus;
  substatuses: Array<string>;
  note: string;
  created: string;
  createdBy: string;
  source: RiskGapSource;
}
